import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

if (!globalThis.__themeStickyHeaderModuleLoaded) {
  globalThis.__themeStickyHeaderModuleLoaded = true;

  /** @type {WeakSet<HTMLElement>} */
  const initializedHeaders = new WeakSet();

  /** @type {WeakSet<HTMLElement>} */
  const initializedBenefits = new WeakSet();

  const updateStickyOffsets = () => {
    const stickyHeader = document.querySelector('#header-group > .header-section.header-section--is-stuck');
    const stickyBenefits = document.querySelectorAll(
      '#header-group .announcement-benefits.announcement-benefits--is-stuck'
    );

    let benefitsOffset = 0;
    stickyBenefits.forEach((element) => {
      if (element instanceof HTMLElement) {
        benefitsOffset += element.offsetHeight;
      }
    });

    document.body.style.setProperty('--sticky-benefits-offset', `${Math.round(benefitsOffset)}px`);
    document.body.style.setProperty(
      '--sticky-main-header-offset',
      stickyHeader instanceof HTMLElement ? `${Math.round(stickyHeader.offsetHeight)}px` : '0px'
    );
  };

  /**
   * Fixed-header behavior for header-component[sticky].
   * CSS position:sticky is unreliable here (#header-group layout, flex body, section wrappers).
   *
   * @param {HTMLElement} headerComponent
   */
  function initStickyHeader(headerComponent) {
    if (initializedHeaders.has(headerComponent)) return;

    const stickyMode = headerComponent.getAttribute('sticky');
    if (!stickyMode) return;

    initializedHeaders.add(headerComponent);

    const headerSection = headerComponent.closest('.header-section');
    if (!(headerSection instanceof HTMLElement)) return;

    const spacer = document.createElement('div');
    spacer.className = 'header-sticky-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    headerSection.insertAdjacentElement('afterend', spacer);

    let announcementOffset = 0;
    let lastScrollTop = 0;
    let scrollRafId = null;

    const measureAnnouncementOffset = () => {
      const headerGroup = document.querySelector('#header-group');
      if (!headerGroup) {
        announcementOffset = 0;
        return;
      }

      let height = 0;
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child === headerSection) break;
        if (child instanceof HTMLElement) {
          height += child.offsetHeight;
        }
      }
      announcementOffset = height;
    };

    const setStuck = (stuck) => {
      headerSection.classList.toggle('header-section--is-stuck', stuck);
      if (stuck) {
        const height = headerSection.offsetHeight;
        spacer.style.height = `${height}px`;
        spacer.style.display = 'block';
      } else {
        spacer.style.display = 'none';
      }
      updateStickyOffsets();
    };

    const updateMetaThemeColor = (stuck) => {
      if (stickyMode !== 'always' || !headerComponent.dataset.themeColor) return;
      if (stuck) {
        changeMetaThemeColor(headerComponent.dataset.themeColor);
      }
    };

    const onScroll = () => {
      const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
      const pastAnnouncements = scrollTop > announcementOffset;
      const isScrollingUp = scrollTop < lastScrollTop;

      if (stickyMode === 'always') {
        setStuck(pastAnnouncements);
        updateMetaThemeColor(pastAnnouncements);
        headerComponent.dataset.stickyState = pastAnnouncements ? 'active' : 'inactive';
        headerComponent.dataset.scrollDirection =
          scrollTop <= announcementOffset ? 'none' : isScrollingUp ? 'up' : 'down';
      } else if (stickyMode === 'scroll-up') {
        if (!pastAnnouncements) {
          setStuck(false);
          headerComponent.dataset.stickyState = 'inactive';
          headerComponent.dataset.scrollDirection = 'none';
        } else if (isScrollingUp) {
          setStuck(true);
          headerComponent.dataset.stickyState = 'active';
          headerComponent.dataset.scrollDirection = 'up';
        } else {
          setStuck(false);
          headerComponent.dataset.stickyState = 'idle';
          headerComponent.dataset.scrollDirection = 'none';
        }
      }

      lastScrollTop = scrollTop;
    };

    const onScrollThrottled = () => {
      if (scrollRafId !== null) return;
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = null;
        onScroll();
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      measureAnnouncementOffset();
      if (headerSection.classList.contains('header-section--is-stuck')) {
        spacer.style.height = `${headerSection.offsetHeight}px`;
      }
      updateStickyOffsets();
      onScrollThrottled();
    });

    resizeObserver.observe(headerComponent);
    resizeObserver.observe(headerSection);

    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child instanceof HTMLElement && child !== headerSection) {
          resizeObserver.observe(child);
        }
      }
    }

    measureAnnouncementOffset();
    onScroll();

    const scrollTarget = document.scrollingElement ?? document.documentElement;
    window.addEventListener('scroll', onScrollThrottled, { passive: true });
    scrollTarget.addEventListener('scroll', onScrollThrottled, { passive: true });

    document.addEventListener('shopify:section:load', measureAnnouncementOffset);
    document.addEventListener('shopify:section:unload', measureAnnouncementOffset);
  }

  /**
   * Fixed-header behavior for the benefits bar.
   *
   * @param {HTMLElement} benefitsBar
   */
  function initStickyBenefits(benefitsBar) {
    if (initializedBenefits.has(benefitsBar)) return;

    const stickyMode = benefitsBar.getAttribute('sticky');
    if (!stickyMode) return;

    initializedBenefits.add(benefitsBar);

    const benefitsSection = benefitsBar.closest('.shopify-section');
    if (!(benefitsSection instanceof HTMLElement)) return;

    const spacer = document.createElement('div');
    spacer.className = 'announcement-benefits-sticky-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    benefitsBar.insertAdjacentElement('afterend', spacer);

    let stickyOffset = 0;
    let lastScrollTop = 0;
    let scrollRafId = null;

    const measureStickyOffset = () => {
      const headerGroup = document.querySelector('#header-group');
      if (!headerGroup) {
        stickyOffset = 0;
        return;
      }

      let height = 0;
      const headerGroupChildren = headerGroup.children;
      for (let i = 0; i < headerGroupChildren.length; i++) {
        const child = headerGroupChildren[i];
        if (child === benefitsSection) break;
        if (child instanceof HTMLElement) {
          height += child.offsetHeight;
        }
      }

      const sectionChildren = benefitsSection.children;
      for (let i = 0; i < sectionChildren.length; i++) {
        const child = sectionChildren[i];
        if (child === benefitsBar) break;
        if (child instanceof HTMLElement) {
          height += child.offsetHeight;
        }
      }

      stickyOffset = height;
    };

    const setStuck = (stuck) => {
      benefitsBar.classList.toggle('announcement-benefits--is-stuck', stuck);
      if (stuck) {
        spacer.style.height = `${benefitsBar.offsetHeight}px`;
        spacer.style.display = 'block';
      } else {
        spacer.style.display = 'none';
      }
      updateStickyOffsets();
    };

    const onScroll = () => {
      const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
      const pastStickyOffset = scrollTop > stickyOffset;
      const isScrollingUp = scrollTop < lastScrollTop;

      if (stickyMode === 'always') {
        setStuck(pastStickyOffset);
        benefitsBar.dataset.stickyState = pastStickyOffset ? 'active' : 'inactive';
        benefitsBar.dataset.scrollDirection =
          scrollTop <= stickyOffset ? 'none' : isScrollingUp ? 'up' : 'down';
      } else if (stickyMode === 'scroll-up') {
        if (!pastStickyOffset) {
          setStuck(false);
          benefitsBar.dataset.stickyState = 'inactive';
          benefitsBar.dataset.scrollDirection = 'none';
        } else if (isScrollingUp) {
          setStuck(true);
          benefitsBar.dataset.stickyState = 'active';
          benefitsBar.dataset.scrollDirection = 'up';
        } else {
          setStuck(false);
          benefitsBar.dataset.stickyState = 'idle';
          benefitsBar.dataset.scrollDirection = 'none';
        }
      }

      lastScrollTop = scrollTop;
    };

    const onScrollThrottled = () => {
      if (scrollRafId !== null) return;
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = null;
        onScroll();
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      measureStickyOffset();
      if (benefitsBar.classList.contains('announcement-benefits--is-stuck')) {
        spacer.style.height = `${benefitsBar.offsetHeight}px`;
      }
      updateStickyOffsets();
      onScrollThrottled();
    });

    resizeObserver.observe(benefitsBar);
    resizeObserver.observe(benefitsSection);

    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child instanceof HTMLElement && child !== benefitsSection) {
          resizeObserver.observe(child);
        }
      }
    }

    const sectionChildren = benefitsSection.children;
    for (let i = 0; i < sectionChildren.length; i++) {
      const child = sectionChildren[i];
      if (child instanceof HTMLElement && child !== benefitsBar) {
        resizeObserver.observe(child);
      }
    }

    measureStickyOffset();
    onScroll();

    const scrollTarget = document.scrollingElement ?? document.documentElement;
    window.addEventListener('scroll', onScrollThrottled, { passive: true });
    scrollTarget.addEventListener('scroll', onScrollThrottled, { passive: true });

    document.addEventListener('shopify:section:load', measureStickyOffset);
    document.addEventListener('shopify:section:unload', measureStickyOffset);
  }

  const initStickyElements = (scope) => {
    const headerComponent = scope.querySelector('#header-component[sticky]');
    if (headerComponent instanceof HTMLElement) {
      initStickyHeader(headerComponent);
    }

    const benefitsBars = scope.querySelectorAll('.announcement-benefits[sticky]');
    benefitsBars.forEach((benefitsBar) => {
      if (benefitsBar instanceof HTMLElement) {
        initStickyBenefits(benefitsBar);
      }
    });

    updateStickyOffsets();
  };

  onDocumentLoaded(() => {
    initStickyElements(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    initStickyElements(event.target);
  });
}
